import React, {
  Dispatch,
  KeyboardEvent,
  MutableRefObject,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Editor from 'draft-js-plugins-editor';
import { DraftHandleValue, EditorState, getDefaultKeyBinding, RichUtils } from 'draft-js';
import { metaTrigger } from '@repo/utils/helpers/keyboardHandler';
import { MentionSuggestion } from './mention';
import { selectContactArray, updateContact } from '@repo/redux/actions/contacts';
import cn from 'classnames';
import 'draft-js/dist/Draft.css';
import 'draft-js-checkable-list-item/lib/CheckableListItem.css';
import {
  buildPlugins,
  editorSelectionStateToClipboardContent,
  editorStateToMarkdown,
  insertPastedContent,
  markdownToEditorState,
} from './utils';
import Occasion from '@repo/utils/helpers/Occasion';
import { useRefSetter, useContactSearch } from '@repo/hooks';
import { selectDefaultReminderTime, selectUserHasFlag } from '@repo/redux/reducers/user/metadata';

export { FollowupIcon } from './FollowupPlugin/Followup';
export { markdownToPlainText } from './utils';

export function ReadOnlyTextEditorContent({
  value,
  updated,
  followupOccasion,
  clickableMentions = true,
}: {
  value?: string;
  updated?: Occasion;
  followupOccasion: Occasion | null;
  clickableMentions?: boolean;
}) {
  const { plugins } = useMemo(
    () => buildPlugins({ readOnly: true, followupOccasion, clickableMentions }),
    [],
  );
  const editorState = useMemo(() => markdownToEditorState(value, plugins), [updated]);
  return <Editor editorState={editorState} readOnly plugins={plugins} onChange={() => {}} />;
}

interface TextEditorProps {
  className?: string;
  content: string;
  onSave?: () => void;
  onContentChange: (content: string) => void;
  inputRef?: MutableRefObject<HTMLInputElement>;
  followupOccasion: Occasion | null;
  setFollowupOccasion: Dispatch<Occasion | null>;
  setFollowupEntityInteracted: Dispatch<boolean>;
  header?: ReactNode;
  readOnly?: boolean;
  disableFollowupParsing?: boolean;
}

export default function TextEditor(props: TextEditorProps) {
  const dispatch = useDispatch();
  const userDisabledFollowupParsing = useSelector(selectUserHasFlag('disableNoteReminderParsing'));
  const defaultReminderTime = useSelector(selectDefaultReminderTime);
  const [mentionSuggestionsOpen, setMentionSuggestionsOpen] = useState(false);
  const delayParsingRef = useRef(false);
  const { MentionSuggestions, plugins } = useMemo(
    () =>
      buildPlugins({
        followupOccasion: props.followupOccasion,
        setFollowupOccasion: props.setFollowupOccasion,
        setFollowupEntityInteracted: props.setFollowupEntityInteracted,
        // for now this is just tied to whether or not the mentionSuggestions menu is open
        delayParsingRef,
        // disable followup parsing
        disableFollowupParsing: userDisabledFollowupParsing || props.disableFollowupParsing,
        defaultReminderTime,
      }),
    [],
  );
  const initialState = useMemo(() => markdownToEditorState(props.content, plugins), []);
  const [editorState, setEditorState] = useState(initialState);
  const [_, setEditorRef] = useRefSetter((node) => {
    if (props.inputRef) {
      props.inputRef.current = node;
    }

    // Always focus an editor instance when it mounts. Unclear why, but we actually
    // error if this call is skipped ¯\_(ツ)_/¯
    node.focus();
  });

  const [setSearchTerm, searchState] = useContactSearch({
    defaultTerm: 'all',
    // searchAttributes: ['displayName'],
    type: 'mentionSearch',
  });

  const suggestions = useSelector(
    selectContactArray(searchState?.results.filteredItems.keySeq().toArray()),
  );

  const updateEditorState = (nextEditorState: EditorState) => {
    props.onContentChange(editorStateToMarkdown(nextEditorState));
    setEditorState(nextEditorState);
  };

  const update = (type: string) => {
    updateEditorState(RichUtils.toggleInlineStyle(editorState, type));
  };

  const handleKeyCommand = (command: string): DraftHandleValue => {
    if (command === 'save') {
      if (props.onSave) {
        updateEditorState(editorState);
        props.onSave();
      }

      return 'handled';
    }

    if (command === 'key-bold') {
      update('BOLD');
      return 'handled';
    }

    if (command === 'key-italic') {
      update('ITALIC');
      return 'handled';
    }

    return 'not-handled';
  };

  // We lose newlines and keep other formatting (markdown) by default. In order to keep
  // both newlines and formatting, we need to mix and match between ContentBlocks created
  // from the plain text input (which gives us newline blocks but no other formatting) and
  // ContentBlocks created from the pasted HTML (which gives us block formatting but no
  // newlines). Inspired by https://stackoverflow.com/a/58234972
  const handlePastedText = (
    text: string,
    html: string | null,
    editorState: EditorState,
  ): DraftHandleValue => {
    const nextEditorState = insertPastedContent(text, html, editorState);
    props.onContentChange(editorStateToMarkdown(nextEditorState));
    setEditorState(nextEditorState);

    return 'handled';
  };

  const keyBindingFn = (e: KeyboardEvent): string | any => {
    if (e.keyCode === 13 && metaTrigger(e)) {
      return 'save';
    }

    if (metaTrigger(e) && e.key === 'b') {
      return 'key-bold';
    }

    if (metaTrigger(e) && e.key === 'i') {
      return 'key-italic';
    }

    // Allows tabbing of bulleted lists
    if (!mentionSuggestionsOpen && e.key == 'Tab') {
      const nextEditorState = RichUtils.onTab(e, editorState, 6);
      if (nextEditorState != editorState) {
        updateEditorState(nextEditorState);
      }
      return;
    }

    // When suggestions menu is open, don't intercept keys that control menu UX
    if (mentionSuggestionsOpen && ['ArrowDown', 'ArrowUp', 'Tab'].includes(e.key)) {
      return;
    }

    // allows us to handle backspace separately with FollowupPlugin
    if (e.key == 'Backspace') {
      return;
    }

    return getDefaultKeyBinding(e);
  };

  const onCopy = (e) => {
    const editorState = e.props.editorState;
    const { html, text } = editorSelectionStateToClipboardContent(editorState);
    navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' }),
      }),
    ]);
  };

  useEffect(() => {
    delayParsingRef.current = mentionSuggestionsOpen;
  }, [mentionSuggestionsOpen]);

  const { inputRef, className, content, ...editorProps } = props;

  return (
    <div className={cn(className, suggestions.length == 0 && 'suggestions-empty')}>
      {props.header}
      <Editor
        spellCheck
        plugins={plugins}
        ref={setEditorRef}
        keyBindingFn={keyBindingFn}
        handleKeyCommand={handleKeyCommand}
        handlePastedText={handlePastedText}
        editorState={editorState}
        onChange={updateEditorState}
        onCopy={onCopy}
        {...editorProps}
      />
      <MentionSuggestions
        open={mentionSuggestionsOpen}
        onOpenChange={setMentionSuggestionsOpen}
        suggestions={suggestions}
        entryComponent={MentionSuggestion}
        renderEmptyPopup
        onSearchChange={({ value }) => {
          setSearchTerm(value);
        }}
        onAddMention={(contact) => {
          // cache the contact data so it is immediately available for consumption
          // by the <Related/> component in contact sidebar
          dispatch(updateContact(contact));
        }}
      />
    </div>
  );
}
